import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigateWithTransition } from '@muzly/shared';

import { DiagnosisCard } from './DiagnosisCard';
import { MetricTiles } from './MetricTiles';
import { ResumeDialog } from './ResumeDialog';
import {
  isResumeDismissed,
  readDismissed,
  writeDismissed,
} from './resumePrompt';
import { TrendChart } from './TrendChart';
import {
  AvatarProfileIcon,
  PullToRefresh,
  TransitionLink,
} from '../../components';
import { DIAGNOSIS_FLOW } from '../onboarding/flow';
import {
  clearProgress,
  loadProgress,
  resumeStep,
  useDiagnoses,
} from '../onboarding/record';
import { RECORD_TESTS } from '../onboarding/recordTests';
import {
  canRetryDiagnosisRestore,
  clearAnalySessionId,
  restoreDiagnoses,
} from '../onboarding/server';
import { draftStore } from '../onboarding/storage';

/**
 * 홈(시안 `1014:9946`, 20260729) — 프로필이 있는 사람의 첫 화면. 진단으로 들어가는 문과
 * 마지막 회차의 요약.
 *
 * **프로필 검사는 여기 없다** — 라우트 게이트(`RequireProfile`)가 한다. 초기 설정을
 * 안 마친 사람은 이 화면에 오지 않고 `/setup`에 선다. 측정환경·오늘의 컨디션은
 * **회차마다 달라서** 진단 플로우가 녹음 직전에 다시 묻는다(flow.ts).
 *
 * 진행 중인 회차가 있으면 들어서자마자 이어하기 팝업(시안 `1048:48431`)이 묻는다.
 * 손상·구버전 진행은 여기까지 오지 않는다 — `loadProgress`가 조용히 버린다.
 *
 * 진행(`progress`)은 화면을 열 때 한 번만 읽는다(HistoryPage와 같은 관례) — 보는 동안
 * 진행이 늘 일이 없다. 회차(`records`)는 **구독형**(`useDiagnoses`)이다 — 부팅 직후
 * 서버 이력 복원(`useDiagnosisRestore`)이 비동기로 끝나므로, 이 화면이 열려 있는 동안
 * 복원이 완료돼도 다시 그려야 한다.
 */

export const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithTransition();
  const [progress] = useState(loadProgress);
  // 닫힘은 이번 실행 동안 기억한다 — 사유와 보관 위치는 `resumePrompt.ts`.
  const [dismissed, setDismissed] = useState(() =>
    progress ? isResumeDismissed(readDismissed(), progress.updatedAt) : false,
  );
  // 브라우저에서는 항상 비어 있다 — mock 세션은 저장하지 않는다(diagnosisStore).
  // 요약(마지막 회차)과 추세(7일)가 **같은 읽기**를 나눠 쓴다.
  const records = useDiagnoses();
  // 추세 창의 오른쪽 끝도 열 때 고정한다 — 매 렌더 Date.now()를 부르면 렌더마다 시계열을
  // 다시 만들고, 화면을 켜 둔 채 자정을 넘기면 칸이 소리 없이 하루 밀린다.
  const [openedAt] = useState(() => Date.now());

  const body = (
    <div className="flex flex-1 flex-col px-[18px] pb-8 pt-[calc(var(--safe-area-top,0px)+1rem)]">
      <header className="flex items-center justify-between px-0.5">
        {/* 시안 §7.1의 레드 워드마크. 글자로 찍지 않는 이유: 이 로고는 자간과 글자꼴이
            서체와 다르게 손본 그림이라, 어떤 폰트로 써도 시안과 다른 모양이 된다.
            `alt`는 앱 이름 그대로다 — 장식이 아니라 이 화면이 어느 앱인지 말한다. */}
        <h1 className="leading-none">
          <img
            src="/brand/wordmark.png"
            alt={t('web.common.appName')}
            className="h-6 w-auto"
          />
        </h1>
        {/* 시안의 36px 회색 아바타. 터치 타깃은 44px로 유지하고 그림만 36px이다.
            프로필은 탭바를 감추는 드릴다운이라 스택 전환을 탄다 — 뒤로가기가 되감는 전환과 짝. */}
        <TransitionLink
          to="/more/profile"
          aria-label={t('web.more.profile')}
          className="-mr-1 flex h-11 w-11 items-center justify-center"
        >
          <AvatarProfileIcon className="h-9 w-9" aria-hidden="true" />
        </TransitionLink>
      </header>

      <div className="mt-8">
        <DiagnosisCard resuming={progress !== null} />
      </div>

      {/* 회차가 없으면 추세도 안 그린다 — 요약 카드와 같은 규칙(빈 껍데기 금지). 회차가
          하나뿐이면 섹션은 뜨되 차트 대신 안내 문구가 뜬다(TrendChart가 판단). */}
      {records.length > 0 && <TrendChart records={records} now={openedAt} />}

      {/* 타일 일곱은 상세 탭과 같은 목록이고 누르면 그 탭으로 간다(시안 §7.1). */}
      {records[0] && <MetricTiles record={records[0]} />}

      {progress && !dismissed && (
        <ResumeDialog
          current={resumeStep(progress)}
          total={RECORD_TESTS.length}
          // "새로 시작" = 녹음 진행도, 회차 답·위치(드래프트)도 버린다 — 진단이 환경 질문부터
          // 다시 연다. 진행만 지우면 드래프트가 측정 자리를 가리켜 질문을 건너뛴다.
          // 서버 루트도 같이 버린다: 버린 회차의 루트를 물려받으면 새 회차의 테이크가 그
          // 회차에 덮어써져, 서버에는 두 회차가 한 회차로 남는다. 프로필은 건드리지 않는다.
          onRestart={() => {
            clearProgress();
            clearAnalySessionId();
            draftStore(DIAGNOSIS_FLOW).clear();
            navigate('/diagnosis');
          }}
          onResume={() => navigate('/diagnosis')}
          onDismiss={() => {
            writeDismissed(progress.updatedAt);
            setDismissed(true);
          }}
        />
      )}
    </div>
  );

  // 당겨서 새로고침은 **회차가 없고 서버가 켜져 있을 때만** 붙인다 — 그때만 당김이 실제로
  // 무언가를 부른다(`canRetryDiagnosisRestore`). 회차가 있으면 화면은 로컬 구독으로 이미
  // 최신이라, 스피너만 도는 제스처가 된다.
  return canRetryDiagnosisRestore(records) ? (
    <PullToRefresh
      className="flex flex-1 flex-col"
      onRefresh={() => restoreDiagnoses()}
    >
      {body}
    </PullToRefresh>
  ) : (
    body
  );
};
